import React, { useEffect, useState } from 'react';
import { abTestingAPI, ABTestingEncrypt } from '../../i18n/environments';
import { v4 as uuidv4 } from 'uuid';
import CryptoJS from 'crypto-js';

export type TestConfigType = {
  testId: string;
  defaultVersion: any;
  abPages: Array<any>;
  startDate: string;
  endDate: string;
  url: string;
  language: boolean;
  trackAll?: boolean;
};

export type ABTestingPayload = {
  is_es?: boolean;
  test_id?: string;
  user_token: string;
  page_version?: string;
  events?: Array<{ event: string; payload: any }>;
};

interface ComponentProps {
  handleAction?: (event: any, email: string, trackClick?: boolean, callback?: (data?: { param: string }) => void, isForm?: boolean) => void;
}

const getRandomInt = (max: number) => Math.floor(Math.random() * Math.floor(max));

const generateUserId = () => {
  const vid = localStorage.getItem('vid');

  if (!vid) {
    const newVid = uuidv4().replace(/-/g, '');
    localStorage.setItem('vid', newVid);
    return newVid;
  }

  return vid;
};

const encryptPayload = (payload: any) => {
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = CryptoJS.enc.Base64.parse(ABTestingEncrypt);

  payload = {
    ...payload,
    api_key: process.env.REACT_APP_API_KEY,
  };

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), key, { iv });

  return {
    encryptedKey: CryptoJS.enc.Base64.stringify(encrypted.ciphertext),
    iv: CryptoJS.enc.Base64.stringify(iv),
  };
};

const sendUserIdToDB = async (payload: ABTestingPayload, callback?: () => void) => {
  try {
    const encryptedBody = encryptPayload(payload);
    const body = {
      ...encryptedBody,
    };
    const response = await fetch(abTestingAPI, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      console.error(response);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Request failed', error);
  } finally {
    if (callback) callback();
  }
};

const ABTesting = ({
  defaultVersion,
  abPages,
  testId,
  startDate,
  endDate,
  url,
  language,
  trackAll,
  ...props
}: TestConfigType & ComponentProps) => {
  const [Component, setComponent] = useState<React.ComponentType<ComponentProps> | null>(null);
  const [repeatRequest, setRepeatRequest] = useState(false);
  const currentDate = new Date();
  const testStartDate = new Date(startDate);
  const testEndDate = new Date(endDate);

  useEffect(() => {
    const currentPath = window.location.pathname;

    if ((currentPath === url || url === 'all') && currentDate >= testStartDate && currentDate <= testEndDate) {
      let pageIndex: any = localStorage.getItem(testId);

      if (trackAll) {
        sessionStorage.setItem('lst', testId);
      }

      if (!pageIndex && testId) {
        pageIndex = getRandomInt(abPages.length);
        localStorage.setItem(testId, pageIndex);
        setRepeatRequest(true);
      }

      setComponent(() => abPages[Number(pageIndex)]);
    } else {
      setComponent(() => defaultVersion);
    }
  }, [testId, abPages, startDate, endDate, url, trackAll]);

  useEffect(() => {
    if (!repeatRequest) {
      //If the user already did the test don't call the api
      return;
    }

    if (currentDate >= testStartDate && currentDate <= testEndDate) {
      const payload = {
        is_es: language,
        test_id: testId,
        user_token: generateUserId(),
        page_version: Component?.displayName ?? null,
      };

      sendUserIdToDB(payload);
    }
  }, [Component, testId]);

  const handleUserAction = async (
    event: any,
    email: string,
    trackClick?: boolean,
    callback?: (data?: { param: string }) => void,
    isForm: boolean = false
  ) => {
    event.preventDefault();
    const userId = localStorage.getItem('vid');
    if (!userId) {
      console.error('User ID', userId);
      return;
    }
    try {
      const payload = {
        events: [
          ...(email ? [{ event: 'email', payload: email }] : []),
          ...(trackClick ? [{ event: 'has_clicked_element', payload: true }] : []),
        ],
        test_id: testId,
        user_token: userId,
        page_version: Component?.displayName ?? null,
      };

      await sendUserIdToDB(payload);
    } catch (error) {
      console.error('Error in handleUserAction:', error);
    } finally {
      let encryptedBody = undefined;
      if (userId && !isForm) {
        encryptedBody = encryptPayload({
          test_id: testId,
          user_token: userId,
          page_version: Component?.displayName ?? null,
        });
      }
      if (callback)
        callback?.({
          param: encryptedBody ? `?ek=${encodeURIComponent(encryptedBody.encryptedKey)}&v=${encodeURIComponent(encryptedBody.iv)}` : '',
        });
    }
  };

  if (!Component) {
    return null;
  }

  return <Component handleAction={handleUserAction} {...props} />;
};

export default ABTesting;
